import { Typography } from '@breetometer/components';
import { CurrencyBalance, FiatIcon } from 'BreetAssets';
import { FiatInput, type IFiatDropDownOption, SendFiat } from 'BreetComponents';
import {
	capitalizeFirstLetter,
	cleanRate,
	convertFxRate,
	type CURRENCYCODETYPE,
	CURRENCYCONFIG,
	formatCurrencyInputAmount,
	FXRATE,
	getFxConversionType,
	getSwapWithdrawRules,
	getWithdrawRules,
} from 'BreetHelpers';
import {
	useCurrencyConverterContext,
	useCurrencyFormAmountApi,
	useCurrentFxRate,
	useUserCurrencyProps,
	useUserWalletApi,
	useWalletBalance,
} from 'BreetHooks';
import { selectMeta, useAppSelector } from 'BreetRedux';
import { type ChangeEvent, memo, useCallback, useEffect, useMemo } from 'react';

import { getCurrencyFromLabel } from '../../DialogCurrencyConverter.helpers';
import { useCurrencyConverterFormStageContext } from '../FormStageContext';

const fiatOptions = [
	{
		label: 'NGN',
		icon: (
			<FiatIcon
				variant='round'
				currencyCode='NGN'
			/>
		),
	},
	{
		label: 'USD',
		icon: (
			<FiatIcon
				variant='round'
				currencyCode='USD'
			/>
		),
	},
	{
		label: 'GHS',
		icon: (
			<FiatIcon
				variant='round'
				currencyCode='GHS'
			/>
		),
	},
];

const FormComponent = () => {
	const {
		formDataContextProps,
		setFromSelectedFiatOption,
		setToSelectedFiatOption,
		fromSelectedFiatOption,
		toSelectedFiatOption,
	} = useCurrencyConverterContext();

	const { navigator } = useCurrencyConverterFormStageContext();

	const currencyProps = useUserCurrencyProps();

	const { getBalance, isWalletBalanceLoading } = useWalletBalance();

	const {
		hookFormProps: {
			formState: { errors },
			watch,
			clearErrors,
			setValue,
			setError,
		},
		setValueAndValidate,
	} = formDataContextProps;

	const amount = watch('amount');
	const toAmount = watch('toAmount');
	const fromCurrency = watch('fromCurrency');
	const toCurrency = watch('toCurrency');

	const fromOptionsObj = useMemo(
		(): Record<CURRENCYCODETYPE, IFiatDropDownOption[]> => ({
			NGN: [fiatOptions[0], fiatOptions[1]],
			GHS: [fiatOptions[2], fiatOptions[1]],
			USD: currencyProps?.code === 'NGN' ? [fiatOptions[0], fiatOptions[1]] : [fiatOptions[2], fiatOptions[1]],
		}),
		[currencyProps?.code]
	);

	const fromOptions: IFiatDropDownOption[] = fromOptionsObj[fromCurrency];

	const toOptionsObj = useMemo(
		(): Record<CURRENCYCODETYPE, { options: IFiatDropDownOption[]; code: CURRENCYCODETYPE }> => ({
			NGN: { options: [fiatOptions[1], fiatOptions[2]], code: 'USD' },
			GHS: { options: [fiatOptions[1], fiatOptions[0]], code: 'USD' },
			USD: {
				options: currencyProps?.code === 'NGN' ? [fiatOptions[0]] : [fiatOptions[2]],
				code: currencyProps?.code as unknown as CURRENCYCODETYPE,
			},
		}),
		[currencyProps?.code]
	);

	const toOptions: IFiatDropDownOption[] = toOptionsObj[fromCurrency].options;

	const conversionType = getFxConversionType(fromCurrency, toCurrency);

	const currentFxRate = useCurrentFxRate({ fxRate: FXRATE.DEFAULT, conversionType });

	const userWalletProps = useUserWalletApi(fromCurrency);

	const meta = useAppSelector(selectMeta);

	const isSwap = conversionType === 'GHSToNGN' || conversionType === 'NGNToGHS';

	const getRulesFn = isSwap ? getSwapWithdrawRules : getWithdrawRules;

	const { withdrawalMaxAmount, withdrawalMinAmount } = getRulesFn(meta?.settings, fromCurrency, fromCurrency === 'USD');

	const { handleAmountChange, commitAmount, validateAmount } = useCurrencyFormAmountApi({
		withdrawalMaxAmount,
		withdrawalMinAmount,
		userWalletProps,
		currencySymbol: currentFxRate.symbol.from,
		currencyCode: fromCurrency,
	});

	const setErrorMessage = useCallback(
		(message: string) => {
			setError('amount', { message });
		},
		[setError]
	);

	const clearErrorMessage = useCallback(() => {
		clearErrors('amount');
	}, [clearErrors]);

	const convertFxToCurrencyValue = useCallback(
		(fromCurrencyValue: string) => {
			if (!currentFxRate.rate) {
				console.error('No rate found');
				return '0';
			}

			const convertedAmount = convertFxRate(
				cleanRate(currentFxRate.rate),
				cleanRate(fromCurrencyValue),
				fromCurrency === 'USD' ? 'from' : 'to'
			);

			setValue('toAmount', convertedAmount.toString());
		},
		[currentFxRate.rate, fromCurrency, setValue]
	);

	const updateFromCurrencyValue = useCallback(
		(type: 'from' | 'to') => (cleanedValue: string) => {
			setValueAndValidate('amount', cleanedValue)
				.then(() => {
					validateAmount(cleanedValue, setErrorMessage, clearErrorMessage);

					if (type === 'from') {
						convertFxToCurrencyValue(cleanedValue);
					}
				})
				.catch(console.error);
		},
		[setValueAndValidate, validateAmount, setErrorMessage, clearErrorMessage, convertFxToCurrencyValue]
	);

	const onSelectFiat = useCallback(
		(type: 'from' | 'to') => (option: IFiatDropDownOption) => {
			const code = getCurrencyFromLabel(option.label);

			if (type === 'from') {
				setFromSelectedFiatOption(option);

				const toOption = toOptionsObj[code];
				setToSelectedFiatOption(toOption.options[0]);

				setValue('fromCurrency', code);
				setValue('toCurrency', toOption.code);
			}

			if (type === 'to') {
				setToSelectedFiatOption(option);
				setValue('toCurrency', code);
			}
		},
		[setFromSelectedFiatOption, setToSelectedFiatOption, setValue, toOptionsObj]
	);

	const handleFiatAmountChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target;

			setValueAndValidate('toAmount', value)
				.then(() => {
					if (!!errors.amount?.message || !value) clearErrors('amount');

					if (!currentFxRate.rate) {
						console.error('No rate found');
						return;
					}

					const fiatAmount = convertFxRate(
						cleanRate(currentFxRate.rate),
						cleanRate(value),
						fromCurrency === 'USD' ? 'to' : 'from'
					);

					commitAmount(fiatAmount.toString(), updateFromCurrencyValue('to'));
				})
				.catch(console.error);
		},
		[
			setValueAndValidate,
			errors.amount?.message,
			clearErrors,
			currentFxRate.rate,
			fromCurrency,
			commitAmount,
			updateFromCurrencyValue,
		]
	);

	const handleCtaClick = useCallback(() => {
		navigator.goTo('transaction-details')();
	}, [navigator]);

	const isFormCompleted =
		!!amount && Number(amount) >= withdrawalMinAmount && !errors.amount?.message && !!toAmount && cleanRate(toAmount) > 0;

	useEffect(() => {
		if (watch('amount') === '0') return;

		validateAmount(watch('amount'), setErrorMessage, clearErrorMessage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [withdrawalMaxAmount, withdrawalMinAmount, fromCurrency]);

	useEffect(() => {
		if (!amount || amount === '0') return;
		convertFxToCurrencyValue(amount);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fromCurrency, toCurrency]);

	const fromCurrencySymbol = CURRENCYCONFIG[fromCurrency].symbol;
	const fromCurrencyShort = CURRENCYCONFIG[fromCurrency].currencyShort;

	return (
		<form className='payment-request-form conversion'>
			<FiatInput.Default
				label='Enter Amount'
				currencyCode={fromCurrency}
				onChange={handleAmountChange(errors.amount?.message ?? '', updateFromCurrencyValue('from'), clearErrorMessage)}
				value={formatCurrencyInputAmount(amount, fromCurrency)}
				errorMessage={errors.amount?.message}
				hideTooltip
				dropDown={{
					defaultOption: fromOptions[0],
					selectedOption: fromSelectedFiatOption,
					options: fromOptions,
					onSelect: onSelectFiat('from'),
					isLoading: isWalletBalanceLoading,
				}}
			/>

			<div className='from-wallet-balance'>
				<Typography className='color-primary-100'>
					{capitalizeFirstLetter(fromCurrencyShort)} Wallet Balance: {fromCurrencySymbol}
					{getBalance(fromCurrency)}
				</Typography>
			</div>

			<FiatInput.Default
				label='You Receive'
				currencyCode={toCurrency}
				onChange={handleFiatAmountChange}
				value={formatCurrencyInputAmount(toAmount, toCurrency)}
				hideTooltip
				dropDown={{
					defaultOption: toOptions[0],
					selectedOption: toSelectedFiatOption,
					options: toOptions,
					onSelect: onSelectFiat('to'),
					isLoading: isWalletBalanceLoading,
				}}
			/>

			<div className='buycrypto-rate bg-primary-600 border-dark-navy'>
				<Typography className='color-primary-100'>Current Rate: {currentFxRate.rateInfo}</Typography>
			</div>

			<div className='currency-balance'>
				<div className='currency-balance_header'>
					<Typography
						as='label'
						className='color-primary-100'
					>
						Destination Wallet
					</Typography>
				</div>
				<CurrencyBalance
					variant={toCurrency}
					balance={getBalance(toCurrency)}
					className='svg-dark svg-dark-outline'
				/>
			</div>

			<SendFiat
				handleCtaClick={handleCtaClick}
				isFormCompleted={isFormCompleted}
			/>
		</form>
	);
};

const Form = memo(FormComponent);

export default Form;
